import React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "../../../lib/cn";
import { Button } from "../ui-v2";

/**
 * Pagination — ui-v2.5. Pie de tabla con recuento, tamaño de página y navegación.
 * Especificación: catálogo · sección Pagination. Va DENTRO del contenedor de la tabla.
 *
 * Encaja con el objeto del backend { page, pageSize, total, totalPages }. Si no se pasa
 * totalPages se calcula con Math.ceil(total / pageSize). Elipsis a partir de 5 páginas.
 * Botones mapeados al Button de ui-v2 (página actual → default, resto → outline).
 *
 * <Pagination page pageSize total totalPages? onPageChange onPageSizeChange? pageSizeOptions? />
 */

function pageList(current, total) {
  const range = (a, b) => Array.from({ length: b - a + 1 }, (_, i) => a + i);
  if (total <= 5) return range(1, total);
  if (current <= 3) return [1, 2, 3, "…", total];
  if (current >= total - 2) return [1, "…", total - 2, total - 1, total];
  return [1, "…", current - 1, current, current + 1, "…", total];
}

function PagBtn({ active, disabled, onClick, icon, children }) {
  return (
    <Button
      type="button"
      variant={active ? "default" : "outline"}
      size="sm"
      disabled={disabled}
      onClick={onClick}
      className={cn("h-auto min-w-0 rounded-v25-md py-1.5 text-xs", icon ? "px-1.5" : "px-2.5")}
    >
      {children}
    </Button>
  );
}

export function Pagination({
  page = 1,
  pageSize = 25,
  total = 0,
  totalPages,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [25, 50, 100],
  className,
}) {
  const pages = Math.max(1, totalPages || Math.ceil(total / pageSize) || 1);
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);

  return (
    <div className={cn("flex flex-wrap items-center justify-between gap-4 border-t border-v25-surface-raised px-4 py-2.5", className)}>
      <span className="text-[11px] text-v25-ink-500">
        Mostrando <b className="font-medium text-v25-ink-700">{from}–{to}</b> de <b className="font-medium text-v25-ink-700">{total}</b>
      </span>
      <div className="flex items-center gap-3">
        {onPageSizeChange && (
          <div className="flex items-center gap-1.5">
            <span className="text-[11px] text-v25-ink-500">Por página</span>
            <select
              value={pageSize}
              onChange={(e) => onPageSizeChange(Number(e.target.value))}
              className="cursor-pointer rounded-v25-md border border-v25-line-strong bg-white px-2 py-1 text-xs outline-none focus:border-v25-focus focus:shadow-v25-focus"
            >
              {pageSizeOptions.map((o) => (
                <option key={o} value={o}>{o}</option>
              ))}
            </select>
          </div>
        )}
        <div className="flex items-center gap-1">
          <PagBtn icon disabled={page <= 1} onClick={() => onPageChange?.(page - 1)}>
            <ChevronLeft className="h-3.5 w-3.5" />
          </PagBtn>
          {pageList(page, pages).map((p, i) =>
            p === "…" ? (
              <span key={`e${i}`} className="px-1 text-[11px] text-v25-ink-400">…</span>
            ) : (
              <PagBtn key={p} active={p === page} onClick={() => onPageChange?.(p)}>{p}</PagBtn>
            )
          )}
          <PagBtn icon disabled={page >= pages} onClick={() => onPageChange?.(page + 1)}>
            <ChevronRight className="h-3.5 w-3.5" />
          </PagBtn>
        </div>
      </div>
    </div>
  );
}
